import ActionButton from "@/components/Button/ActionButton";
import ToggleButton from "@/components/Button/ToggleButton";
import { Card, CardContent } from "@/components/Card/card";
import {
  createFilterDefHelper,
  useComposableFilter,
} from "@/components/ComposableFilter";
import Icon from "@/components/Icon/Icon";
import { HStack } from "@/components/Layout";
import { DeviceGenreFilterChip } from "@/features/device/components/DeviceGenreFilterChip";
import {
  DeviceSearchInput,
  ErrorDeviceToggle,
  ReportStatusSelect,
  UnassignedDeviceToggle,
} from "@/features/device/device-filters";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { DeviceToolbarProps, DeviceToolbarSearchParams } from "../types";

const helper = createFilterDefHelper<DeviceToolbarSearchParams>();

const filterDefs = [
  helper("freeText", { component: DeviceSearchInput }),
  helper("deviceGenres", { component: DeviceGenreFilterChip }),
  helper("assigned", { component: UnassignedDeviceToggle }),
  helper("reportStatus", { component: ReportStatusSelect }),
  helper("hasError", { component: ErrorDeviceToggle }),
];

export function DeviceToolbar({
  searchParams,
  onSearchChange,
  filterConfig,
  actionConfig,
  className,
}: DeviceToolbarProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "domain.device.components.deviceToolbar",
  });

  const filter = useComposableFilter({
    filterDefs,
    filterState: searchParams,
    onFilterStateChange: onSearchChange,
  });

  const hasConditionalFilters =
    filterConfig?.unassignedDevice ||
    filterConfig?.reportStatus ||
    filterConfig?.errorDevice;

  const hasActions =
    actionConfig?.refresh?.enabled || actionConfig?.toggleSummary?.enabled;

  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="p-3">
        <HStack className="justify-between">
          <HStack className="gap-4 flex-1">
            <DeviceSearchInput
              value={filter.filterState.freeText}
              onChange={filter.getOnChangeHandler("freeText")}
            />

            <DeviceGenreFilterChip
              value={filter.filterState.deviceGenres}
              onChange={filter.getOnChangeHandler("deviceGenres")}
            />

            {hasConditionalFilters && (
              <>
                {filterConfig.unassignedDevice && (
                  <UnassignedDeviceToggle
                    value={filter.filterState.assigned}
                    onChange={filter.getOnChangeHandler("assigned")}
                  />
                )}

                {filterConfig.reportStatus && (
                  <ReportStatusSelect
                    value={filter.filterState.reportStatus}
                    onChange={filter.getOnChangeHandler("reportStatus")}
                  />
                )}

                {filterConfig.errorDevice && (
                  <ErrorDeviceToggle
                    value={filter.filterState.hasError}
                    onChange={filter.getOnChangeHandler("hasError")}
                  />
                )}
              </>
            )}
          </HStack>

          {/* 右側: アクション */}
          {hasActions && (
            <HStack className="gap-2">
              {actionConfig?.refresh?.enabled ? (
                <ActionButton
                  variant="ghost"
                  size="sm"
                  onClick={actionConfig.refresh.onRefresh}
                >
                  <Icon iconName="RefreshCwIcon" className="w-4 h-4" />
                </ActionButton>
              ) : null}

              {actionConfig?.toggleSummary?.enabled && (
                <ToggleButton
                  label={t("showSummary")}
                  checked={actionConfig.toggleSummary.isVisible}
                  onCheckedChange={actionConfig.toggleSummary.onToggle}
                />
              )}
            </HStack>
          )}
        </HStack>
      </CardContent>
    </Card>
  );
}
